import mixpanel from "mixpanel-browser";
import { MIXPANEL_PROD_TOKEN, MIXPANEL_DEV_TOKEN } from "../constants";
import { getApplication } from "../../../util/application";
import { analyticsServiceLog } from "../utils/analyticsLog";
import { getErrorMessageOrDefault } from "@vortex/shared";
class MixpanelAnalytics {
    user;
    isInitialized = false;
    /**
     * isUserSet returns if the user is set
     */
    isUserSet() {
        return !!this.user && this.isInitialized;
    }
    /**
     * Sets and Initializes the Mixpanel tracking with super properties
     */
    start(userInfo, isProduction) {
        // Guard against multiple initialization
        if (this.isInitialized) {
            analyticsServiceLog("mixpanel", "warn", "start() called but already initialized", {
                userId: this.user,
                newUserId: userInfo.userId,
            });
            return;
        }
        this.user = userInfo.userId;
        const token = isProduction ? MIXPANEL_PROD_TOKEN : MIXPANEL_DEV_TOKEN;
        const environment = isProduction ? "production" : "development";
        // Initialize mixpanel-browser with config
        mixpanel.init(token, {
            debug: false, // Disable internal Mixpanel logging (we use our own analyticsServiceLog)
            track_pageview: false, // We're not a web page
            persistence: "localStorage",
            api_host: "https://api-eu.mixpanel.com",
            // IP and geolocation are automatically tracked by mixpanel-browser
        });
        this.isInitialized = true;
        // Identify the user
        mixpanel.identify(this.user.toString());
        // Build and register super properties
        const superProperties = this.buildSuperProperties(userInfo);
        mixpanel.register(superProperties);
        analyticsServiceLog("mixpanel", "debug", `Started for ${environment}`, {
            userId: this.user,
            isProduction,
            environment,
            superProperties,
        });
    }
    /**
     * Build super properties according to data team specs
     */
    buildSuperProperties(userInfo) {
        // Identity & Session
        const userType = this.getUserType(userInfo);
        // isModAuthor unavailable
        // isStaff unavailable
        // Subscription
        // premiumStatus unavailable
        // planType unavailable
        // Platform
        const platformType = "app"; // Always 'app' for Vortex
        const appName = "Vortex";
        const appVersion = getApplication().version;
        const superProps = {
            // Identity & Session
            user_type: userType,
            // isModAuthor unavailable
            // isStaff unavailable
            // Subscription
            // premiumStatus unavailable
            // planType unavailable
            // Platform
            platform_type: platformType,
            app_name: appName,
            app_version: appVersion,
        };
        return superProps;
    }
    /**
     * Determine user type from user info
     */
    getUserType(userInfo) {
        if (!userInfo)
            return "anonymous"; // unused as always logged in before sending
        if (userInfo.isPremium)
            return "premium";
        if (userInfo.isSupporter)
            return "supporter";
        return "registered"; // free
    }
    /**
     * Update super properties (e.g., when game changes)
     */
    updateSuperProperties(properties) {
        if (!this.isUserSet())
            return;
        mixpanel.register(properties);
    }
    /**
     * Disable tracking
     */
    stop() {
        if (this.isInitialized) {
            try {
                mixpanel.reset(); // Clears user identity and super properties
            }
            catch (err) {
                analyticsServiceLog("mixpanel", "warn", "Failed to reset mixpanel", {
                    error: getErrorMessageOrDefault(err),
                });
            }
        }
        this.user = null;
        this.isInitialized = false;
    }
    /**
     * Track an event using event instance
     */
    trackEvent(event) {
        if (!this.isUserSet()) {
            // Silently ignore when analytics is disabled (user opted out)
            // This is expected behavior, not an error condition
            analyticsServiceLog("mixpanel", "debug", "Event not tracked (analytics disabled)", { eventName: event.eventName });
            return;
        }
        // Track event with mixpanel-browser
        // Super properties are automatically included
        // IP address and geolocation are automatically tracked
        mixpanel.track(event.eventName, event.properties);
        analyticsServiceLog("mixpanel", "debug", "Event tracked", {
            eventName: event.eventName,
            properties: event.properties,
        });
    }
}
const analyticsMixpanel = new MixpanelAnalytics();
export default analyticsMixpanel;
